<template>

    <div class="container">

        <div id="messages"></div>

        <div class="page-header clearfix h1 mt-10">
                <i class="fa fa-chart-area"></i>
                Production Stats
                <div class="header-buttons">
                    <button v-if="table.main.rows.length" @click="columnVisibilityModal" class="btn green">
                        Columns
                    </button>
                </div>
                <div class="header-loading-icon">
                    <loading-spinner v-if="loading"></loading-spinner>
                </div>
        </div>

        <div class="ps-filter-row row-filter">

            <div class="ps-filter-col col-filter col-filter-active">
                <div class="col-filter-label">Time Range</div>
                <v-select
                        :options="all_time_spans"
                        :getOptionLabel="toTitleCase"
                        v-model="time_span"
                        :disabled="loading"
                        :searchable="false"
                        :clearable="false"
                        class="tt-filter"
                ></v-select>
            </div>

            <div class="ps-filter-col col-filter col-filter-active">
                <div class="col-filter-label">Time Unit</div>
                <v-select
                        :options="all_time_units"
                        :getOptionLabel="toTitleCase"
                        v-model="time_unit"
                        :disabled="loading"
                        :searchable="false"
                        :clearable="false"
                        class="tt-filter"
                ></v-select>
            </div>

            <div class="ps-filter-col col-filter" :class="{'col-filter-active': haveUserNames}">
                <div class="col-filter-label">Group By People</div>
                <v-select
                        :options="userNameOptions"
                        :disabled="loading"
                        v-model="user_names"
                        :searchable="false"
                        :clearable="true"
                        :multiple="true"
                        class="tt-filter ps-select-multiple"
                >
                </v-select>
            </div>

            <div class="ps-filter-col col-filter" :class="{'col-filter-active': haveProcessTags}">
                <div class="col-filter-label">Group By Processes</div>
                <v-select
                        :options="processTagOptions"
                        :getOptionLabel="toTitleCase"
                        v-model="process_tags"
                        :disabled="loading"
                        :searchable="true"
                        :clearable="true"
                        :multiple="true"
                        class="tt-filter ps-select-multiple"
                ></v-select>
            </div>

            <div class="ps-filter-col-btn col-filter">
                <button :disabled="loading"
                        class="btn btn-ps-search"
                        @click="loadTable">
                    GO
                </button>
            </div>

        </div>


        <div class="row pb-30">
            <div class="col-md-12">

                <div v-if="!firstTimeLoading" class="ps-table-head">
                    <i class="fas fa-chart-area"></i>
                    {{ chartYAxisLabel }} {{ chart.title_end }}
                </div>

                <production-stats-chart
                        v-if="!firstTimeLoading"
                        :chart-data="chartData"
                        :options="chartOptions"
                        css-classes="ps-chart"
                        :height="500"
                />
            </div>
        </div>


        <div class="row pb-30">
            <div class="col-md-12">

                <div v-if="!firstTimeLoading" class="ps-table-head">
                    <i class="fas fa-table"></i>
                    Totals
                </div>

                <vue-good-table
                        v-if="!firstTimeLoading"
                        @on-selected-rows-change="statsTotalsTableCheckboxesChanged"
                        styleClass="vgt-table ps-totals-table"
                        :row-style-class="totalsRowStyles"
                        :columns="totalsTableColumns"
                        :rows="table.totals.rows"
                        ref="ps-totals-table"
                        :sort-options="{
                            enabled: true,
                        }"
                        :selectOptions="{
                            enabled: true,
                            selectionInfoClass: 'ps-table-selection-info',
                        }"
                        :group-options="{
                            enabled: true,
                            headerPosition: 'bottom',
                        }"
                >
                    <div slot="emptystate">
                        <div class="table-empty">
                            <span v-if="loading">
                                Loading...
                            </span>
                            <span v-else>
                                No rows found
                            </span>
                        </div>
                    </div>

                    <template slot="table-column" slot-scope="props">

                        <template v-if="props.column.field.indexOf('avg_') !== -1 || props.column.field.indexOf('total_') !== -1">
                            <i class="fas fa-chart-area change-metric-icon"
                               @click.stop="chart.metric = props.column.field"></i>
                            {{ props.column.label }}
                        </template>

                        <template v-else>
                            {{ props.column.label }}
                        </template>

                    </template>

                    <template slot="table-row" slot-scope="props">

                        <span v-if="props.column.field === 'user_id' && props.row.user_id">
                            {{ allUsersById[props.row.user_id].name }}
                        </span>

                        <span v-else-if="props.column.slotType === 'seconds'">
                            {{ props.formattedRow[props.column.field] | format_seconds }}
                        </span>

                        <span v-else-if="props.column.slotType === 'pence'">
                            £{{ props.formattedRow[props.column.field] / 100 }}
                        </span>

                        <span v-else-if="props.column.slotType === 'percent'">
                            {{ props.formattedRow[props.column.field] }}%
                        </span>

                        <span v-else-if="props.column.slotType === 'title_case'">
                            {{ props.formattedRow[props.column.field] | title_case }}
                        </span>

                        <span v-else>
                            {{props.formattedRow[props.column.field]}}
                        </span>

                        <span v-if="props.column.field === 'avg_seconds_per_m' || props.column.field === 'avg_seconds_per_m2'">
                            ({{ props.formattedRow[props.column.field] | format_seconds_as_mins }})
                        </span>

                    </template>

                </vue-good-table>

            </div>
        </div>



        <div class="row pb-90">
            <div class="col-md-12">

                <div v-if="!firstTimeLoading" class="ps-table-head">
                    <i class="fas fa-table"></i>
                    All
                </div>

                <vue-good-table
                        v-if="!firstTimeLoading"
                        styleClass="vgt-table ps-main-table"
                        :columns="mainTableColumns"
                        :rows="table.main.rows"
                        :sort-options="{
                            enabled: true,
                        }"
                        :selectOptions="{
                            enabled: false,
                        }"
                >
                    <div slot="emptystate">
                        <div class="table-empty">
                            <span v-if="loading">
                                Loading...
                            </span>
                            <span v-else>
                                No rows found
                            </span>
                        </div>
                    </div>

                    <template slot="table-row" slot-scope="props">

                        <span v-if="props.column.field === 'user_id' && props.row.user_id">
                            {{ allUsersById[props.row.user_id].name }}
                        </span>

                        <span v-else-if="props.column.slotType === 'seconds'">
                            {{ props.formattedRow[props.column.field] | format_seconds }}
                        </span>

                        <span v-else-if="props.column.slotType === 'pence'">
                            £{{ props.formattedRow[props.column.field] / 100 }}
                        </span>

                        <span v-else-if="props.column.slotType === 'percent'">
                            {{ props.formattedRow[props.column.field] }}%
                        </span>

                        <span v-else-if="props.column.slotType === 'title_case'">
                            {{ props.formattedRow[props.column.field] | title_case }}
                        </span>

                        <span v-else>
                            {{props.formattedRow[props.column.field]}}
                        </span>

                        <span v-if="props.column.field === 'avg_seconds_per_m' || props.column.field === 'avg_seconds_per_m2'">
                            ({{ props.formattedRow[props.column.field] | format_seconds_as_mins }})
                        </span>

                    </template>

                </vue-good-table>

            </div>
        </div>

        <modals-container
            @selected-columns="columnVisibilityModalSelectedColumns"
        />

    </div>

</template>

<script>

    let Vue = window.Vue;

    import 'vue-good-table/dist/vue-good-table.css'
    import { VueGoodTable } from 'vue-good-table';
    import api from '../api';
    import LoadingSpinner from './LoadingSpinner';
    import {default as _} from "lodash";
    import vSelect from "vue-select";
    import 'vue-select/dist/vue-select.css';
    import miniToastr from 'mini-toastr';
    import ProductionStatsChart from "./ProductionStatsChart";
    import ColumnVisibilityForm from "./ColumnVisibilityForm";
    import VModal from 'vue-js-modal'
    Vue.use(VModal, { dynamic: true });
    import VueLocalStorage from 'vue-localstorage';
    Vue.use(VueLocalStorage);

    export default {

        props: {
            all_users: {
                type: Object,
                required: true
            },
            all_process_tags: {
                type: Array,
                required: true
            },
            all_time_spans: {
                type: Array,
                required: true
            },
            all_time_units: {
                type: Array,
                required: true
            },
        },

        data: () => ({
            time_span: 'week',
            time_unit: 'day',
            user_names: null,
            process_tags: null,

            loading: true,
            firstTimeLoading: true,
            selected_rows: [],
            selected_column: null,
            app_url: window.location.protocol + "//" + window.location.hostname,

            visible_column_keys: [],

            chart: {
                labels: [], // x-axis
                metric: 'avg_seconds', // y-axis
                options: {},
                title_end: '',
            },

            table: {
                main: {
                    rows: [],
                    columns: [
                        {
                            label: 'Date Value',
                            field: 'date_value',
                            type: 'string',
                        },
                        {
                            label: 'Person',
                            field: 'user_id',
                            type: 'number',
                            hidden: true,
                            tdClass: 'vgt-left-align',
                            thClass: 'vgt-left-align',
                        },
                        {
                            label: 'Process Tag',
                            field: 'process_tag',
                            type: 'string',
                            slotType: 'title_case',
                            hidden: true,
                        },
                        {
                            label: 'Processes',
                            field: 'process_count',
                            type: 'number',
                            tdClass: 'vgt-center-align',
                            thClass: 'vgt-center-align',
                        },
                    ],
                },
                totals: {
                    rows: [],
                    columns: [
                        {
                            label: 'Person',
                            field: 'user_id',
                            type: 'number',
                            hidden: true,
                            tdClass: 'vgt-left-align',
                            thClass: 'vgt-left-align',
                        },
                        {
                            label: 'Process Tag',
                            field: 'process_tag',
                            type: 'string',
                            slotType: 'title_case',
                            hidden: true,
                        },
                        {
                            label: 'Processes',
                            field: 'process_count',
                            type: 'number',
                            tdClass: 'vgt-center-align',
                            thClass: 'vgt-center-align',
                        },
                    ],
                },
            },

        }),

        components: {
            ProductionStatsChart,
            VueGoodTable,
            vSelect,
            miniToastr,
            LoadingSpinner,
            ColumnVisibilityForm,
            VModal,
            VueLocalStorage,
        },

        mounted() {
            $(document).ready(function() {
                $("#toggle-full-width").click();

                miniToastr.init({
                    timeout: 3000,
                    appendTarget: document.getElementById('messages'),
                });
            });

            this.loadVisibleColumnKeys();
        },

        async created() {
            await this.loadTable(true);
        },

        methods: {
            loadVisibleColumnKeys() {
                let value = JSON.parse(this.$localStorage.get('visible_column_keys', '[]'));

                this.visible_column_keys = value.length ? value : [
                    'avg_seconds',
                    'avg_percent_seconds',
                    'avg_gross_profit',
                    'avg_percent_gross_margin',
                ];
            },
            loadTable(force=false) {
                if (!force && this.loading)
                    return false;

                this.prepareColumns();
                this.loadStats();
            },
            loadStats() {
                this.loading = true;
                return api.getProductionStats(
                    this.time_span,
                    this.time_unit,
                    this.selectedUserIds,
                    this.process_tags
                ).then(data => {
                    this.loading = false;
                    this.firstTimeLoading = false;
                    if (api.responseHasErrors(data)) return;
                    this.chart.labels = data.period;
                    this.table.main.rows = data.main;
                    this.setTotalsRows(data.totals, data.grand_totals);
                }).then(this.postLoadSetup);
            },
            setTotalsRows(totals, grand_totals) {
                // Single grouped row, so we can utilize the summary footer
                this.table.totals.rows = [{
                    item_count: grand_totals.item_count,
                    process_count: grand_totals.process_count,
                    
                    avg_seconds: Vue.filter('format_seconds')(grand_totals.avg_seconds),
                    avg_time_cost: Vue.filter('format_pence')(grand_totals.avg_time_cost),
                    avg_est_seconds: Vue.filter('format_seconds')(grand_totals.avg_est_seconds),
                    avg_materials_cost: Vue.filter('format_pence')(grand_totals.avg_materials_cost),
                    avg_cost: Vue.filter('format_pence')(grand_totals.avg_cost),
                    avg_time_price: Vue.filter('format_pence')(grand_totals.avg_time_price),
                    avg_materials_price: Vue.filter('format_pence')(grand_totals.avg_materials_price),
                    avg_sale_price: Vue.filter('format_pence')(grand_totals.avg_sale_price),
                    avg_gross_profit: Vue.filter('format_pence')(grand_totals.avg_gross_profit),
                    avg_percent_seconds: grand_totals.avg_percent_seconds + '%',
                    avg_percent_gross_margin: grand_totals.avg_percent_gross_margin + '%',
                    avg_seconds_per_m: Vue.filter('format_seconds')(grand_totals.avg_seconds_per_m)
                        + ' (' +Vue.filter('format_seconds_as_mins')(grand_totals.avg_seconds_per_m) + ')',
                    avg_seconds_per_m2: Vue.filter('format_seconds')(grand_totals.avg_seconds_per_m2)
                        + ' (' +Vue.filter('format_seconds_as_mins')(grand_totals.avg_seconds_per_m2) + ')',

                    total_seconds: Vue.filter('format_seconds')(grand_totals.total_seconds),
                    total_time_cost: Vue.filter('format_pence')(grand_totals.total_time_cost),
                    total_est_seconds: Vue.filter('format_seconds')(grand_totals.total_est_seconds),
                    total_materials_cost: Vue.filter('format_pence')(grand_totals.total_materials_cost),
                    total_cost: Vue.filter('format_pence')(grand_totals.total_cost),
                    total_time_price: Vue.filter('format_pence')(grand_totals.total_time_price),
                    total_materials_price: Vue.filter('format_pence')(grand_totals.total_materials_price),
                    total_sale_price: Vue.filter('format_pence')(grand_totals.total_sale_price),
                    total_gross_profit: Vue.filter('format_pence')(grand_totals.total_gross_profit),
                    total_percent_seconds: grand_totals.total_percent_seconds + '%',
                    total_percent_gross_margin: grand_totals.total_percent_gross_margin + '%',
                    total_seconds_per_m: Vue.filter('format_seconds')(grand_totals.total_seconds_per_m),
                    total_seconds_per_m2: Vue.filter('format_seconds')(grand_totals.total_seconds_per_m2),
                    
                    children: totals,
                }];
            },
            postLoadSetup() {
                this.totalsTableSelectAll();
                this.updateChartTitleEnd();
            },
            totalsTableSelectAll() {
                let table = this.$refs['ps-totals-table'];
                if (! table.allSelected)
                    table.toggleSelectAll();
            },
            prepareColumns() {
                this.table.main.columns[1].hidden = ! this.haveUserNames;
                this.table.main.columns[2].hidden = ! this.haveProcessTags;

                this.table.totals.columns[0].hidden = ! this.haveUserNames;
                this.table.totals.columns[1].hidden = ! this.haveProcessTags;
            },
            columnVisibilityModal() {
                this.$modal.show(ColumnVisibilityForm, {
                    all_columns: this.allToggleableColumnKeys,
                    selected_columns: this.visible_column_keys,
                }, {
                    clickToClose: false,
                    width: '60%',
                    height: 'auto',
                    scrollable: true,
                    adaptive: true,
                });
            },
            columnVisibilityModalSelectedColumns(event) {
                this.visible_column_keys = event;
                this.totalsTableSelectAll();
            },
            ucwords(string) {
                return _.startCase(_.toLower(string));
            },
            updateChartTitleEnd() {
                let title =  ' for ';

                if (! this.haveUserNames && ! this.haveProcessTags)
                    title += 'Everything';

                if (this.haveUserNames && this.haveProcessTags)
                    title += 'People & Processes';

                if (! this.haveUserNames && this.haveProcessTags)
                    title += 'Processes';

                if (this.haveUserNames && ! this.haveProcessTags)
                    title += 'People';

                title += ' by ' + this.ucwords(this.time_unit);

                this.chart.title_end = title;
            },
            statsTotalsTableCheckboxesChanged(params) {
                this.selected_rows = params.selectedRows;
            },
            toTitleCase(str) {
                return Vue.filter('title_case')(str);
            },
            getDataSeries(user_id=undefined, process_tag=undefined) {
                return _.filter(this.table.main.rows, function(row) {
                    return row.user_id === user_id && row.process_tag === process_tag;
                })
                    .map(row => this.formatChartMetric(row[this.chart.metric]));
            },
            formatChartMetric(metric) {

                // seconds to minutes
                if (this.chartMetricType === 'seconds')
                    return parseFloat(metric / 60).toPrecision(3);

                // pence to pounds
                if (this.chartMetricType === 'pence')
                    return metric / 100;

                return metric;
            },
            getDataSeriesLabel(user_id=undefined, process_tag=undefined) {
                if (! user_id && ! process_tag)
                    return 'All';

                let label = [];

                if (user_id)
                    label.push(this.allUsersById[user_id].name);

                if (process_tag)
                    label.push(Vue.filter('title_case')(process_tag));

                return label.join(':');
            },
            getDataSeriesObject(user_id=undefined, process_tag=undefined) {
                return {
                    spanGaps: true,
                    label: this.getDataSeriesLabel(user_id, process_tag),
                    backgroundColor: this.getRandomRGBColor(`${user_id} ${process_tag}`, 0.5),
                    data: this.getDataSeries(user_id, process_tag),
                };
            },
            getRandomRGBColor(seed=null, opacity=1) {
                seed = seed
                    ? parseFloat('0.' + this.numericHash(seed))
                    : Math.random();

                let hex = '#' + Math.floor(seed*16777215).toString(16);
                let rgb = this.hexToRgb(hex);

                return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${opacity})`;
            },
            numericHash(str) {
                return Math.abs(str.split('').reduce((prevHash, currVal) =>
                    (((prevHash << 5) - prevHash) + currVal.charCodeAt(0))|0, 0));
            },
            hexToRgb(hex) {
                // Expand shorthand form (e.g. "03F") to full form (e.g. "0033FF")
                let shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
                hex = hex.replace(shorthandRegex, function(m, r, g, b) {
                    return r + r + g + g + b + b;
                });

                let result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
                return result ? {
                    r: parseInt(result[1], 16),
                    g: parseInt(result[2], 16),
                    b: parseInt(result[3], 16)
                } : null;
            },
            totalsRowStyles(row) {
                return row.vgtSelected === true ? 'ps-selected-row' : '';
            },
            metricLabel(metric) {
                let str = metric
                    .replace(/seconds/g, 'time')
                    .replace(/percent/g, 'pct')
                    .replace(/_/g, ' ');
                return this.ucwords(str)
                    .replace(/Avg/, 'AVG')
                    .replace(/Pct/, 'PCT');
            },
            metricType(metric) {
                if (metric.indexOf('percent') !== -1)
                    return 'percent';

                if (metric.indexOf('seconds') !== -1)
                    return 'seconds';

                if (
                    metric.indexOf('cost') !== -1
                    || metric.indexOf('price') !== -1
                    || metric.indexOf('profit') !== -1
                )
                    return 'pence';

                return 'number';
            },
        },

        computed: {
            allToggleableColumnKeys() {
                return _.keys(this.table.main.rows[0]).filter(key => {
                    return _.startsWith(key, 'avg_') || _.startsWith(key, 'total_')
                });
            },
            totalsTableColumns() {
                let columns = this.table.totals.columns.concat(this.optionalColumns);

                return columns.map(col => {
                    let styleClass = col.field === this.chart.metric ? 'ps-selected-column' : '';
                    col.tdClass = styleClass;
                    col.thClass = styleClass;
                    return col;
                });
            },
            mainTableColumns() {
                return this.table.main.columns.concat(this.optionalColumns);
            },
            optionalColumns() {
                return this.visible_column_keys.map(col => {
                    return {
                        label: this.metricLabel(col),
                        field: col,
                        type: 'number',
                        slotType: this.metricType(col),
                    }
                });
            },
            chartMetricType() {
                return this.metricType(this.chart.metric);
            },
            statsTableClassObject() {
                return 'vgt-table';// + (this.loading ? 'load-fade ' : '');
            },
            haveUserNames() {
                return this.user_names && this.user_names.length > 0;
            },
            haveProcessTags() {
                return this.process_tags && this.process_tags.length > 0;
            },
            userNameOptions() {
                return _.concat(['[ALL]'], _.map(this.all_users, user => user.name));
            },
            processTagOptions() {
                return _.concat(['[CATEGORIES]', '[TAGS]', '[PROCESSES]'], this.all_process_tags);
            },
            allUsersById() {
                return _.keyBy(this.all_users, user => user.id);
            },
            selectedUserIds() {
                let ids = [];
                let usersByName = _.mapKeys(this.all_users, user => user.name);

                _.forEach(this.user_names, function(name) {
                    let value = _.startsWith(name, '[') ? name : usersByName[name].id;
                    ids.push(value);
                });

                return ids;
            },
            chartData() {
                return {
                    labels: this.chart.labels,
                    datasets: this.chartDataSets
                };
            },
            chartDataSets() {
                let sets = [];
                let getDataSeriesObject = this.getDataSeriesObject;

                _.forEach(this.selected_rows, function(row) {
                    sets.push(getDataSeriesObject(row.user_id, row.process_tag));
                });

                return sets;
            },
            chartXAxisLabel() {
                let str = this.time_unit + 's';
                return Vue.filter('title_case')(str);
            },
            chartYAxisLabel() {
                return this.metricLabel(this.chart.metric).replace(/Time/, 'Minutes');
            },
            chartOptions() {
                return {
                    responsive: true,
                    maintainAspectRatio: false,
                    legend: {
                        labels: {
                            padding: 20,
                            fontSize: 13,
                        },
                    },
                    scales: {
                        xAxes: [{
                            gridLines: {
                                display: false,
                            },
                            // scaleLabel: {
                            //     display: true,
                            //     labelString: this.chartXAxisLabel,
                            // }
                        }],
                        yAxes: [{
                            gridLines: {
                                color: "rgb(223,223,223)",
                            },
                            // scaleLabel: {
                            //     display: true,
                            //     labelString: this.chartYAxisLabel,
                            // }
                        }],
                    },
                };
            },
        },

        watch: {
            visible_column_keys: function () {
                this.$localStorage.set('visible_column_keys', JSON.stringify(this.visible_column_keys));
            }
        }

    }
</script>

<style>

    .ps-totals-table td,
    .ps-totals-table thead th,
    .ps-main-table thead th {
        cursor: pointer;
    }

    .ps-chart {
        padding: 10px 0 0 0;
    }

    .ps-select-multiple .vs__selected {
        background-color: #e9e9e9;
        border: 1px solid rgba(0, 0, 0, 0.26);
        padding: 0.05em .45em;
        font-size: 14px;
        color: #000;
    }

    .ps-select-multiple .vs__deselect {
        fill: rgb(179, 179, 179);
        margin-left: 7px;
        padding: 3px;
    }

    .ps-select-multiple .vs__deselect:hover {
        fill: rgb(255, 0, 0);
    }

    .change-metric-icon {
        cursor: pointer;
        margin-right: 5px;
        font-size: 20px;
        color: #757575;
    }

    .ps-selected-row,
    .ps-selected-column {
        background-color: #fffeea;
    }
    .ps-selected-row .ps-selected-column {
        background-color: #ffefb4;
        font-weight: bold;
        color: #595841 !important;
    }

    .ps-selected-row td, .ps-selected-column {
        color: #111111 !important;
    }
    th.ps-selected-column {
        color: #212121 !important;
    }
    .ps-selected-column .change-metric-icon {
        color: #2a91e9;
    }

    .ps-table-selection-info {
        display: none;
    }

    #ProductionStats table.vgt-table td {
        color: #3c3d3f;
        font-size: 17px;
    }

    #ProductionStats .page-header {
        font-size: 32px;
    }

    .ps-table-head {
        margin: 30px 0 20px;
        font-size: 20px;
        color: #4d4d4d;
        padding: 0 10px;
    }
    .ps-table-head .fa,
    .ps-table-head .fas {
        margin-right: 10px;
        color: #929292;
    }

    .btn-ps-search {
        font-size: 18px;
        padding: 8px 25px;
        display: block;
        margin: 0 0 -26px 0;
        background-color: white;
        color: #106c10;
        border: 2px solid #106c10;
        font-weight: bold;
    }
    .btn-ps-search:hover {
        background-color: #307e30;
        color: #fff;
        border-color: #104210;
    }

    .ps-filter-row {
        display: flex;
        align-items: center;
        flex-direction: row;
        justify-content: space-between;
    }
    .ps-filter-col {
        flex-grow: 1;
        max-width: 20%;
    }
    .ps-filter-col-btn {
        flex-grow: 0;
        max-width: 20%;
    }

    .load-fade {
        opacity: 0.5;
    }
    .table-item-name {
    }
    .clickable-item-name {
        cursor: pointer;
    }


    .table-txt-wrap {
        color: #5f5f5f;
        font-size: 15px;
    }
    .table-txt-wrap .fa {
        margin-right: 10px;
        width: 25px;
        text-align: center;
        color: #b6b6b6;
        font-size: 16px;
        display: none;
        vertical-align: top;
        margin-top: 2px;
    }
    @media (min-width:992px) {
        .table-txt-wrap .fa {
            display: inline-block;
        }
    }
    .table-txt-row {
        padding: 3px 0;
        display: block;
        white-space: nowrap;
        width: 90%;
    }
    .table-txt {
        display: inline-block;
        white-space: normal;
        width: calc(100% - 40px);
    }
    .table-txt-main {
        color: #292929;
        font-weight: bold;
        font-size: 16px;
    }
    .table-txt-main .fa {
        color: #242424;
    }

    .table-txt-a {
        color: #a8a8a8 !important;
        font-weight: bold !important;
    }

    .process_icon {
        width: 22px;
        margin: -3px 10px 0 3px;
    }

    .tt-filter-active {}

    .tt-filter .vs__selected {
        white-space: nowrap;
    }
    .tt-filter .vs__selected-options {
        overflow: hidden;
        flex-wrap: nowrap;
    }
    .tt-filter .vs__state-active-bg {
        overflow: hidden;
    }


    .row-filter {
        margin-bottom: 20px;
    }
    .col-filter {
        margin-top: 15px;
    }
    @media (min-width:992px) {
        .col-filter {
            margin-top: 0;
        }
    }

    .col-filter-label {
        font-size: 18px;
        margin: 0 0 8px 0;
        color: #505050;
    }
    .col-filter-badge {
        margin: 0 0 0 10px;
        background-color: #bfbfbf;
        color: #fff;
        border-radius: 4px;
        display: inline-block;
        font-size: 12px;
        vertical-align: top;
    }


    .user-icon {
        font-size: 24px;
        color: #818181;
        display: none;
    }
    .user-avatar-small {
        width: 24px;
        height: 24px;
        border-radius: 4px;
        display: none;
    }
    @media (min-width: 1200px) {
        .user-icon, .user-avatar-small {
            display: inline-block;
        }
    }
    .dropdown-badge {
        margin: 0 0 0 10px;
        background-color: #2b4eb1;
        color: #e7eaf5;
        font-size: 70%;
        font-weight: bold;
        padding: 3px 6px;
        border-radius: 5px;
    }



    .badge-status, a.badge-status {
        border-radius: 4px;
        background-color: #9b9696;
        color: #fff;
        margin: 4px 10px 0 0;
        opacity: 1;
    }
    .badge-status:hover, a.badge-status:hover,
    .badge-status:active, a.badge-status:active,
    .badge-status:focus, a.badge-status:focus {
        color: #fff;
        text-decoration: none;
    }
    .badge-status.faded {
        display: none;
        opacity: 0.3;
    }
    .badge-status.faded:hover {
        opacity: 1;
    }
    @media (min-width:992px) {
        .badge-status.faded {
            display: inline-block;
        }
    }
    .badge-status-new, a.badge-status-new {
        background-color: #9b9696;
    }
    .badge-status-new:hover, a.badge-status-new:hover {
        background-color: #777373;
    }
    .badge-status-paused, a.badge-status-paused {
        background-color: #2f74c0;
    }
    .badge-status-paused:hover, a.badge-status-paused:hover {
        background-color: #265e9b;
    }
    .badge-status-running, a.badge-status-running {
        background-color: #f8c227;
        color: #000;
    }
    .badge-status-running:hover, a.badge-status-running:hover {
        background-color: #cea120;
        color: #000;
    }
    .badge-status-complete, a.badge-status-complete {
        background-color: #289b29;
    }
    .badge-status-complete:hover, a.badge-status-complete:hover {
        background-color: #207a21;
    }
    .badge-status-cancelled, a.badge-status-cancelled {
        background-color: #ad4343;
    }
    .badge-status-cancelled:hover, a.badge-status-cancelled:hover {
        background-color: #ad4343;
    }

    .table-avatar {
        width: 22px;
        height: 22px;
        display: block;
        float: left;
        overflow: hidden;
        margin: 2px 4px;
        border-radius: 4px;
        font-size: 22px;
        text-align: center;
    }
    @media (min-width: 992px) {
        .table-avatar {
            width: 32px;
            height: 32px;
            font-size: 32px;
        }
    }

    .nopoint {
        pointer-events: none;
    }
    .large-status {
    }
    .large-status .vs__open-indicator,
    .large-status .vs__actions,
    .large-status .vs__search {

    }
    .large-status .vs__search {
        padding: 0;
    }
    .large-status .vs__dropdown-toggle {
        border: none;
    }
    .large-status.vs--single.vs--open .vs__selected {
        position: relative;
    }
    .large-status .vs__dropdown-option--selected,
    .large-status .vs__dropdown-option,
    .large-status .vs__dropdown-option--highlight,
    .large-status .vs__dropdown-option--selected:hover,
    .large-status .vs__dropdown-option:hover,
    .large-status .vs__dropdown-option--highlight:hover {
        background-color: #fff;
    }

    .large-status.vs--disabled .vs__clear, .large-status.vs--disabled .vs__dropdown-toggle,
    .large-status.vs--disabled .vs__open-indicator, .large-status.vs--disabled .vs__search,
    .large-status.vs--disabled .vs__selected {
        background-color: #fff;
        opacity: 0.9;
    }
    .btn.disabled, .btn[disabled], fieldset[disabled] .btn {
        filter: alpha(opacity=90) !important;
        opacity: .9 !important;
    }

    .vs__dropdown-option {
        padding: 6px 20px;
        font-size: 18px;
    }

    .vs__clear {
        fill: rgba(95, 95, 95, 0.5);
        padding: 0;
        border: 1px solid #e0e0e0;
        background-color: #e7e7e7;
        cursor: pointer;
        margin: 0 4px 0 0;
        width: 25px;
        height: 25px;
        border-radius: 20px;
    }
    @media (min-width: 1200px) {
        .vs__clear {
            margin: 0 12px 0 0;
        }
    }


    .vselect-center-badge {
        /*margin: 4px 10px 0 11px;*/
    }
    .btn-large-status {
        color: #fff;
        font-size: 35px;
        padding: 6px 16px;
        margin: 0 10px 0 0;
    }
    .btn-large-status .fa {
        color: #fff;
    }
    .btn-large-status:hover, .btn-large-status:active .btn-large-status:focus {
        color: #fff;
    }
    .large-status-badge {
        font-size: 15px;
        padding: 5px 10px;
    }
    @media (min-width: 1200px) {
        .large-status-badge {
            font-size: 18px;
        }
    }

    table.vgt-table .valign-middle {
        vertical-align: middle;
    }

    .vgt-table thead th.sorting-asc::after, .vgt-table thead th.sorting-desc::after {
        display: inline-block;
    }
    .vgt-table th.sorting::after {
        right: inherit;
        margin-left: 10px;
    }

    .deadline-td .fa {
        color: #b4b4b4;
    }

    .header-loading-icon {
        display: inline-block;
        float: right;
        width: 200px;
        margin: 5px 0 0 0;
    }

    .mini-toastr {
        position: absolute !important;
        right: 0 !important;
        top: 0 !important;
    }
    .mini-toastr-notification__message {
        padding: 0 !important;
    }
    #messages {
        position: relative;
    }
    .mini-toastr__notification.-success {
        background-color: #15831a !important;
    }

    .table-selection-info {
        border: 1px solid #cfcfb9;
        padding: 30px 15px 10px 45px;
        font-size: 16px;
        color: #282908;
        border-radius: 4px 4px 0 0;
        border-bottom: 0;
    }
    .table-selection-info .vgt-selection-info-row__actions {
        width: 70%;
        text-align: right;
        margin: -20px 0 0 0;
    }

    .vs__dropdown-toggle {
        padding: 5px 2px 9px 2px;
    }
    @media (min-width: 1200px) {
        .vs__dropdown-toggle {
            padding: 5px 8px 9px 8px;
        }
    }
    .col-filter-active .vs__dropdown-toggle {
        border: 1px solid #9ea193;
    }
    .col-filter-active .col-filter-label {
        font-weight: bold;
        color: #3f3f3f;
    }

    .assignment-select {
        display: inline-block;
        width: 190px;
        background-color: #fff;
        color: #000;
        font-weight: normal;
        margin: 5px 15px;
    }
    .assignment-select .vs__dropdown-toggle {
        border: 1px solid #939548;
        padding-left: 14px;
        padding-right: 14px;
    }

    .btn-action {
        display: inline-block;
        margin: 1px 15px 5px 15px;
        font-size: 16px;
        padding: 9px 20px;
        border-radius: 4px;
        color: #fff;
    }
    .btn-action i.fa {
        padding-right: 10px;
    }
    .btn-action-destructive {
        background-color: #d85f5f;
        border: 1px solid #b44949;
    }
    .btn-action-destructive:hover,
    .btn-action-destructive:active,
    .btn-action-destructive:focus {
        background-color: #bc0e0e;
        border: 1px solid #3b0a0a;
        color: #fff;
    }

    .table-empty {
        font-size: 25px;
        color: #9f9f9f;
        text-align: center;
        min-height: 60px;
        line-height: 400%;
    }


    .people-td {
        width: 65px;
    }
    .quantity-td {
        width: 65px;
    }
    .main-text-td {
        min-width: 300px;
    }
    .assigned-td {
        width: 80px;
    }
    .progress-td {
        width: 150px;
    }
    .status-td {
        width: 150px;
    }

    @media (min-width:1200px) {
        .people-td {
            width: 95px;
        }
        .people-th:after {
            content: "People";
        }
        .quantity-td {
            width: 80px;
        }
        .progress-td {
            width: auto;
        }
        .status-td {
            width: 180px;
        }
    }

    .process_help {
        margin: 0 6px;
        opacity: 0.5;
        display: inline-block;
    }
    .process_help:hover {
        opacity: 1;
    }

    .header-buttons {
        display: inline-block;
        margin: 0 0 0 40px;
        float: right;
    }
    .header-buttons button,
    .header-buttons a {
        display: inline-block;
        font-weight: bold;
        color: #c5c5c5;
        background-color: #f2f2f2;
        border: 2px solid #dbdbdb;
    }
    .header-buttons .btn-green:hover {
        color: #fff;
        background-color: #1c8a21;
        border-color: #0c4e0f;
    }
    .header-buttons .btn-red:hover {
        color: #fff;
        background-color: #a42424;
        border: 2px solid #531010;
    }

    .v--modal {
        background-color: #fff;
        text-align: left;
        border-radius: 8px;
        box-shadow: 0 20px 60px -2px rgba(27, 33, 58, 0.6);
        padding: 20px 40px;
    }
    .v--modal-overlay .v--modal-box {
        overflow: visible;
    }

    .btn-new-issue {
        background-color: #fff;
        color: #eaeaea !important;
        font-size: 16px;
        display: block;
        text-align: center;
        width: 100%;
    }
    .btn-new-issue:hover {
        color: #c22e2e !important;
    }

</style>